import { get, set } from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'
import dayjs from 'dayjs'

export class Meeting {
	_class = 'Meeting'
	loaded = -1
	id = ''
	compact = {}
	full = {}
	to_delete = false
	modified = null

	/**
	 * constructor
	 * @description Initializes a new instance of the Meeting class with observable properties and MobX bindings.
	 * @param {string} id - Unique identifier for the meeting item.
	 */
	constructor(id) {
		this.id = id
		this.compact = {}
		this.full = {}

		makeObservable(this, {
			id: observable,
			compact: observable,
			full: observable,
			to_delete: observable,
			modified: observable,

			// Computed properties  for accessing data in compact/full
			name: computed,
			email: computed,
			phone: computed,
			date: computed,
			hour: computed,
			note: computed,
			progress: computed,
			createdBy: computed,
			createdTs: computed,
			modifiedTs: computed,

			// Action methods  for setting properties
			setName: action.bound,
			setEmail: action.bound,
			setPhone: action.bound,
			setDate: action.bound,
			setHour: action.bound,
			setNote: action.bound,
			setProgress: action.bound,
			setCreatedBy: action.bound,
			setCreatedTs: action.bound,
			setToDelete: action.bound,
			setModified: action.bound,

			updateFromServer: action.bound,
			loaded: observable,
			resetLastFullLoad: action.bound
		})
	}

	// Computed properties with fallback to compact/full
	/**
	 * get name
	 * @description Retrieves the name of the meeting from `full` or falls back to `compact`.
	 * @returns {string}
	 */
	get name() {
		return get(this.full, 'name', get(this.compact, 'name', ''))
	}

	/**
	 * setName
	 * @description Sets the name of the meeting in the `full` object.
	 * @param {string} value
	 */
	setName(value) {
		this.chgInFull('name', value)
	}

	/**
	 * get email
	 * @description Retrieves the email associated with the meeting from `full` or `compact`.
	 * @returns {string}
	 */
	get email() {
		return get(this.full, 'email', get(this.compact, 'email', ''))
	}

	/**
	 * setEmail
	 * @description Sets the email address in the `full` object.
	 * @param {string} value
	 */
	setEmail(value) {
		this.chgInFull('email', value)
	}

	/**
	 * get phone
	 * @description Retrieves the phone number from the meeting data.
	 * @returns {string}
	 */
	get phone() {
		return get(this.full, 'phone', get(this.compact, 'phone', ''))
	}

	/**
	 * setPhone
	 * @description Sets the phone number in the `full` object.
	 * @param {string} value
	 */
	setPhone(value) {
		this.chgInFull('phone', value)
	}

	/**
	 * get date
	 * @description Retrieves the scheduled meeting date from the meeting data.
	 * @returns {string}
	 */
	get date() {
		return get(this.full, 'date', get(this.compact, 'date', ''))
	}

	/**
	 * setDate
	 * @description Sets the date of the meeting in the `full` object.
	 * @param {string} value
	 */
	setDate(value) {
		this.chgInFull('date', value)
	}

	/**
	 * get hour
	 * @description Retrieves the scheduled hour of the meeting.
	 * @returns {string}
	 */
	get hour() {
		return get(this.full, 'hour', get(this.compact, 'hour', ''))
	}

	/**
	 * setHour
	 * @description Sets the hour of the meeting in the `full` object.
	 * @param {string} value
	 */
	setHour(value) {
		this.chgInFull('hour', value)
	}

	/**
	 * get note
	 * @description Retrieves any additional notes associated with the meeting.
	 * @returns {string}
	 */
	get note() {
		return get(this.full, 'note', get(this.compact, 'note', ''))
	}

	/**
	 * setNote
	 * @description Sets a note related to the meeting.
	 * @param {string} value
	 */
	setNote(value) {
		this.chgInFull('note', value)
	}

	/**
	 * get progress
	 * @description Gets the current progress status of the meeting (e.g., pending).
	 * @returns {string}
	 */
	get progress() {
		return get(
			this.full,
			'progress',
			get(this.compact, 'progress', 'pending')
		)
	}

	/**
	 * setProgress
	 * @description Updates the progress status of the meeting.
	 * @param {string} value
	 */
	setProgress(value) {
		this.chgInFull('progress', value)
	}

	/**
	 * get createdBy
	 * @description Retrieves the ID of the user who created the meeting.
	 * @returns {string}
	 */
	get createdBy() {
		return get(this.full, 'createdBy', get(this.compact, 'createdBy', ''))
	}

	/**
	 * setCreatedBy
	 * @description Sets the ID of the user who created the meeting.
	 * @param {string} value
	 */
	setCreatedBy(value) {
		this.chgInFull('createdBy', value)
	}

	/**
	 * get createdTs
	 * @description Gets the Unix timestamp representing when the meeting was created.
	 * @returns {number|null}
	 */
	get createdTs() {
		const createdTs = get(
			this.full,
			'createdTs',
			get(this.compact, 'createdTs', null)
		)
		return createdTs ? dayjs(createdTs).unix() : null
	}

	/**
	 * setCreatedTs
	 * @description Sets the creation timestamp of the meeting.
	 * @param {string|Date} value
	 */
	setCreatedTs(value) {
		this.chgInFull('createdTs', value)
	}

	/**
	 * get modifiedTs
	 * @description Returns the Unix timestamp of the last modification date.
	 * @returns {number|null}
	 */
	get modifiedTs() {
		return this.modified ? dayjs(this.modified).unix() : null
	}

	/**
	 * setModified
	 * @description Updates the `modified` property of the meeting.
	 * @param {string|Date} value
	 */
	setModified(value) {
		this.modified = value
	}

	/**
	 * setToDelete
	 * @description Sets or unsets the deletion flag for the meeting.
	 * @param {boolean|number} value
	 */
	setToDelete(value) {
		this.to_delete = value
	}

	// Basic Methods
	/**
	 * setNewFlag
	 * @description Marks the meeting as newly created by setting the `loaded` flag to -2.
	 */
	setNewFlag = () => {
		this.loaded = -2
	}

	/**
	 * resetLastFullLoad
	 * @description Sets the `loaded` property to the current timestamp to indicate last full data load.
	 */
	resetLastFullLoad = () => {
		this.loaded = dayjs().unix()
	}

	/**
	 * updateFromServer
	 * @description Updates the meeting instance from server response data, parsing `compact` and `full` if necessary.
	 * @param {object} srvJson - JSON object containing server response data.
	 */
	updateFromServer(srvJson) {
		if (typeof srvJson.compact === 'string') {
			try {
				this.compact = JSON.parse(srvJson.compact)
				this.full = this.compact || {}
			} catch (error) {
				console.error('Failed to parse compact JSON:', error)
				this.compact = {}
			}
		} else {
			this.compact = srvJson.compact || {}
			this.full = srvJson.compact || {}
		}

		if (typeof srvJson.full === 'string') {
			try {
				this.full = JSON.parse(srvJson.full)
			} catch (error) {
				console.error('Failed to parse full JSON:', error)
				this.full = {}
			}
		} else {
			this.full = srvJson.full || {}
		}

		this.to_delete = srvJson.to_delete || false
		this.modified = srvJson.modified || null
	}

	/**
	 * chgInFull
	 * @description Helper function to safely update a value inside the `full` object while preserving observability.
	 * @param {string|Array} path - Dot-notated path or array to the property.
	 * @param {*} value - New value to assign.
	 */
	chgInFull(path, value) {
		if (get(this.full, path) !== value) {
			set(this.full, path, value)
		}
	}
}
